import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Truck, 
  Save, 
  CheckCircle, 
  XCircle,
  Loader2,
  Settings,
  MapPin,
  Package,
  FileText,
  AlertCircle,
  RefreshCw,
  Plus,
  Trash2,
  Edit2,
  IndianRupee,
  Star,
  MoreVertical,
  Upload,
  Download,
  FileSpreadsheet,
  Hash,
  List
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CustomCourierIntegrationPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [integrations, setIntegrations] = useState([]);
  const fileInputRef = useRef(null);
  const pincodeFileInputRef = useRef(null);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState(null);
  const [saving, setSaving] = useState(false);
  const [awbDialogOpen, setAwbDialogOpen] = useState(false);
  const [pincodeDialogOpen, setPincodeDialogOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    courier_name: '',
    tracking_url_template: '',
    label_format: 'a4',
    cod_enabled: true,
    is_active: true,
    notes: ''
  });
  
  // AWB form state
  const [awbInput, setAwbInput] = useState('');
  const [awbList, setAwbList] = useState([]);
  
  // Pincode form state
  const [pincodeInput, setPincodeInput] = useState('');
  const [pincodeList, setPincodeList] = useState([]);
  const [allPincodesEnabled, setAllPincodesEnabled] = useState(true);

  useEffect(() => {
    loadIntegrations();
  }, []);

  const loadIntegrations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/couriers/custom-couriers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIntegrations(response.data.integrations || []);
    } catch (error) {
      console.error('Error loading custom couriers:', error);
      toast.error('Failed to load custom couriers');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.courier_name) {
      toast.error('Please enter account name and courier name');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        courier_type: 'custom'
      };

      if (editingIntegration) {
        await axios.put(
          `${API_BASE_URL}/couriers/custom-couriers/${editingIntegration.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Custom courier updated');
      } else {
        await axios.post(
          `${API_BASE_URL}/couriers/custom-couriers`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Custom courier added');
      }

      setDialogOpen(false);
      resetForm();
      loadIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save custom courier');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (integration) => {
    if (!window.confirm(`Are you sure you want to delete "${integration.name}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_BASE_URL}/couriers/custom-couriers/${integration.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Custom courier deleted');
      loadIntegrations();
    } catch (error) {
      toast.error('Failed to delete custom courier');
    }
  };

  const handleEdit = (integration) => {
    setEditingIntegration(integration);
    setFormData({
      name: integration.name || '',
      courier_name: integration.courier_name || '',
      tracking_url_template: integration.tracking_url_template || '',
      label_format: integration.label_format || 'a4',
      cod_enabled: integration.cod_enabled !== false,
      is_active: integration.is_active !== false,
      notes: integration.notes || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      courier_name: '',
      tracking_url_template: '',
      label_format: 'a4',
      cod_enabled: true,
      is_active: true,
      notes: ''
    });
    setEditingIntegration(null);
  };

  const openAwbDialog = async (integration) => {
    setSelectedIntegration(integration);
    setAwbInput('');
    // Load existing AWBs
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/couriers/custom-couriers/${integration.id}/awb`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAwbList(response.data.awb_numbers || []);
    } catch (error) {
      setAwbList([]);
    }
    setAwbDialogOpen(true);
  };

  const openPincodeDialog = async (integration) => {
    setSelectedIntegration(integration);
    setPincodeInput('');
    // Load existing pincodes
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/couriers/custom-couriers/${integration.id}/pincodes`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPincodeList(response.data.pincodes || []);
      setAllPincodesEnabled(response.data.all_pincodes_enabled !== false);
    } catch (error) {
      setPincodeList([]);
      setAllPincodesEnabled(true);
    }
    setPincodeDialogOpen(true);
  };

  const handleAwbUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API_BASE_URL}/couriers/custom-couriers/${selectedIntegration.id}/awb/upload`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );

      toast.success(`Uploaded ${response.data.added_count} AWB numbers`);
      setAwbList(response.data.awb_numbers || []);
      loadIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload AWB file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleAwbManualAdd = async () => {
    if (!awbInput.trim()) {
      toast.error('Please enter AWB numbers');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      // Parse AWB numbers (comma, newline, or space separated)
      const awbNumbers = awbInput
        .split(/[,\n\s]+/)
        .map(awb => awb.trim())
        .filter(awb => awb.length > 0);

      const response = await axios.post(
        `${API_BASE_URL}/couriers/custom-couriers/${selectedIntegration.id}/awb`,
        { awb_numbers: awbNumbers },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Added ${response.data.added_count} AWB numbers`);
      setAwbList(response.data.awb_numbers || []);
      setAwbInput('');
      loadIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add AWB numbers');
    } finally {
      setUploading(false);
    }
  };

  const handlePincodeUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API_BASE_URL}/couriers/custom-couriers/${selectedIntegration.id}/pincodes/upload`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );

      toast.success(`Uploaded ${response.data.added_count} pincodes`);
      setPincodeList(response.data.pincodes || []);
      setAllPincodesEnabled(false);
      loadIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload pincode file');
    } finally {
      setUploading(false);
      if (pincodeFileInputRef.current) {
        pincodeFileInputRef.current.value = '';
      }
    }
  };

  const handlePincodeManualAdd = async () => {
    if (!pincodeInput.trim()) {
      toast.error('Please enter pincodes');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      // Parse pincodes (comma, newline, or space separated)
      const pincodes = pincodeInput
        .split(/[,\n\s]+/)
        .map(p => p.trim())
        .filter(p => p.length > 0);

      const response = await axios.post(
        `${API_BASE_URL}/couriers/custom-couriers/${selectedIntegration.id}/pincodes`,
        { pincodes: pincodes, all_pincodes_enabled: false },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Added ${response.data.added_count} pincodes`);
      setPincodeList(response.data.pincodes || []);
      setAllPincodesEnabled(false);
      setPincodeInput('');
      loadIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add pincodes');
    } finally {
      setUploading(false);
    }
  };

  const handleAllPincodesToggle = async (enabled) => {
    if (!selectedIntegration) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/couriers/custom-couriers/${selectedIntegration.id}/pincodes/settings`,
        { all_pincodes_enabled: enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAllPincodesEnabled(enabled);
      toast.success(enabled ? 'All pincodes enabled' : 'Using pincode list only');
      loadIntegrations();
    } catch (error) {
      toast.error('Failed to update pincode settings');
    }
  };

  const clearAwbList = async () => {
    if (!window.confirm('Are you sure you want to clear all AWB numbers?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_BASE_URL}/couriers/custom-couriers/${selectedIntegration.id}/awb`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAwbList([]);
      toast.success('AWB list cleared');
      loadIntegrations();
    } catch (error) {
      toast.error('Failed to clear AWB list');
    }
  };

  const clearPincodeList = async () => {
    if (!window.confirm('Are you sure you want to clear all pincodes?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_BASE_URL}/couriers/custom-couriers/${selectedIntegration.id}/pincodes`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPincodeList([]);
      toast.success('Pincode list cleared');
      loadIntegrations();
    } catch (error) {
      toast.error('Failed to clear pincode list');
    }
  };

  const downloadSampleAwbFile = () => {
    const sampleData = 'AWB Number\n1234567890\n1234567891\n1234567892\n1234567893\n1234567894';
    const blob = new Blob([sampleData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_awb_list.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const downloadSamplePincodeFile = () => {
    const sampleData = 'Pincode\n110001\n110002\n110003\n400001\n400002';
    const blob = new Blob([sampleData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_pincode_list.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-wt-bg flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-wt-gold" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-wt-bg">
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/integrations')}
            className="text-wt-text-secondary hover:text-wt-text-primary"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg">
              <Truck className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-wt-text-primary">Custom Courier</h1>
              <p className="text-sm text-wt-text-secondary">Add your own courier without API integration</p>
            </div>
          </div>
        </div>

        {/* Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-xl p-6 mb-6"
        >
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <Package className="h-5 w-5 text-purple-400" />
            </div>
            <div>
              <h3 className="text-wt-text-primary font-semibold mb-1">Custom Courier Integration</h3>
              <p className="text-sm text-wt-text-secondary">
                Create custom courier accounts for couriers without API. Upload AWB numbers in bulk 
                or enter them manually during booking. Optionally restrict to specific pincodes.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Add New Button */}
        <div className="flex justify-end mb-6">
          <Button
            onClick={() => {
              resetForm();
              setDialogOpen(true);
            }}
            className="bg-wt-gold hover:bg-wt-gold-hover text-black"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Custom Courier
          </Button>
        </div>

        {/* Integrations List */}
        <div className="space-y-4">
          {integrations.length === 0 ? (
            <div className="bg-wt-surface border border-wt-border rounded-xl p-12 text-center">
              <Truck className="h-16 w-16 mx-auto text-wt-text-muted mb-4 opacity-50" />
              <h3 className="text-lg font-semibold text-wt-text-primary mb-2">No Custom Couriers</h3>
              <p className="text-wt-text-secondary mb-4">Add a custom courier to get started</p>
              <Button
                onClick={() => {
                  resetForm();
                  setDialogOpen(true);
                }}
                className="bg-wt-gold hover:bg-wt-gold-hover text-black"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Custom Courier
              </Button>
            </div>
          ) : (
            integrations.map((integration) => (
              <motion.div
                key={integration.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-wt-surface border border-wt-border rounded-xl p-6"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                      <Truck className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-wt-text-primary">{integration.name}</h3>
                        {integration.is_active ? (
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Active</Badge>
                        ) : (
                          <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Inactive</Badge>
                        )}
                      </div>
                      <p className="text-sm text-wt-text-secondary">
                        Courier: {integration.courier_name}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-wt-text-secondary">
                          <MoreVertical className="h-5 w-5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-wt-surface border-wt-border">
                        <DropdownMenuItem 
                          onClick={() => handleEdit(integration)}
                          className="text-wt-text-primary hover:bg-wt-subtle cursor-pointer"
                        >
                          <Edit2 className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => navigate(`/integrations/shipping-rate-cards?open=${integration.id}`)}
                          className="text-wt-text-primary hover:bg-wt-subtle cursor-pointer"
                          data-testid={`custom-courier-rate-card-${integration.id}`}
                        >
                          <IndianRupee className="h-4 w-4 mr-2" />
                          Rate Card
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleDelete(integration)}
                          className="text-red-400 hover:bg-red-500/10 cursor-pointer"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                {/* Stats Row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-4 border-t border-wt-border">
                  <div 
                    className="p-4 bg-wt-paper rounded-lg cursor-pointer hover:bg-wt-subtle transition-colors"
                    onClick={() => openAwbDialog(integration)}
                  >
                    <div className="flex items-center gap-2 text-wt-text-secondary mb-1">
                      <Hash className="h-4 w-4" />
                      <span className="text-xs">Available AWBs</span>
                    </div>
                    <p className="text-2xl font-bold text-wt-gold">{integration.available_awb_count || 0}</p>
                    <p className="text-xs text-wt-text-muted mt-1">Click to manage</p>
                  </div>
                  
                  <div 
                    className="p-4 bg-wt-paper rounded-lg cursor-pointer hover:bg-wt-subtle transition-colors"
                    onClick={() => openPincodeDialog(integration)}
                  >
                    <div className="flex items-center gap-2 text-wt-text-secondary mb-1">
                      <MapPin className="h-4 w-4" />
                      <span className="text-xs">Serviceable Pincodes</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-400">
                      {integration.all_pincodes_enabled ? 'All' : (integration.pincode_count || 0)}
                    </p>
                    <p className="text-xs text-wt-text-muted mt-1">Click to manage</p>
                  </div>
                  
                  <div className="p-4 bg-wt-paper rounded-lg">
                    <div className="flex items-center gap-2 text-wt-text-secondary mb-1">
                      <Package className="h-4 w-4" />
                      <span className="text-xs">Total Booked</span>
                    </div>
                    <p className="text-2xl font-bold text-wt-text-primary">{integration.total_booked || 0}</p>
                  </div>
                  
                  <div className="p-4 bg-wt-paper rounded-lg">
                    <div className="flex items-center gap-2 text-wt-text-secondary mb-1">
                      <FileText className="h-4 w-4" />
                      <span className="text-xs">Label Format</span>
                    </div>
                    <p className="text-lg font-semibold text-wt-text-primary uppercase">{integration.label_format || 'A4'}</p>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-wt-surface border-wt-border max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-wt-text-primary">
                {editingIntegration ? 'Edit Custom Courier' : 'Add Custom Courier'}
              </DialogTitle>
              <DialogDescription className="text-wt-text-secondary">
                Configure your custom courier settings
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-wt-text-secondary">Account Name *</Label>
                <Input
                  placeholder="e.g., Local Delivery Partner"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="bg-wt-paper border-wt-border text-wt-text-primary"
                />
                <p className="text-xs text-wt-text-muted">Internal name to identify this account</p>
              </div>

              <div className="space-y-2">
                <Label className="text-wt-text-secondary">Courier Name *</Label>
                <Input
                  placeholder="e.g., ABC Logistics"
                  value={formData.courier_name}
                  onChange={(e) => setFormData({...formData, courier_name: e.target.value})}
                  className="bg-wt-paper border-wt-border text-wt-text-primary"
                />
                <p className="text-xs text-wt-text-muted">Name that will appear on labels and tracking</p>
              </div>

              <div className="space-y-2">
                <Label className="text-wt-text-secondary">Tracking URL Template</Label>
                <Input
                  placeholder="e.g., https://example.com/track?awb={awb}"
                  value={formData.tracking_url_template}
                  onChange={(e) => setFormData({...formData, tracking_url_template: e.target.value})}
                  className="bg-wt-paper border-wt-border text-wt-text-primary"
                />
                <p className="text-xs text-wt-text-muted">Use {'{awb}'} as placeholder for AWB number</p>
              </div>

              <div className="space-y-2">
                <Label className="text-wt-text-secondary">Label Format</Label>
                <Select
                  value={formData.label_format}
                  onValueChange={(value) => setFormData({...formData, label_format: value})}
                >
                  <SelectTrigger className="bg-wt-paper border-wt-border text-wt-text-primary">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-wt-surface border-wt-border">
                    <SelectItem value="a4" className="text-wt-text-primary">A4 (Full Page)</SelectItem>
                    <SelectItem value="4x6" className="text-wt-text-primary">4x6 (Thermal)</SelectItem>
                    <SelectItem value="a6" className="text-wt-text-primary">A6 (Half Page)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-wt-text-secondary">Notes</Label>
                <Textarea
                  placeholder="Any additional notes..."
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="bg-wt-paper border-wt-border text-wt-text-primary"
                  rows={2}
                />
              </div>

              <div className="flex items-center justify-between pt-2">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.cod_enabled}
                    onCheckedChange={(checked) => setFormData({...formData, cod_enabled: checked})}
                  />
                  <Label className="text-wt-text-secondary cursor-pointer">COD Enabled</Label>
                </div>
                
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                  />
                  <Label className="text-wt-text-secondary cursor-pointer">Active</Label>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="border-wt-border text-wt-text-secondary"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-wt-gold hover:bg-wt-gold-hover text-black"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                {editingIntegration ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* AWB Management Dialog */}
        <Dialog open={awbDialogOpen} onOpenChange={setAwbDialogOpen}>
          <DialogContent className="bg-wt-surface border-wt-border max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-wt-text-primary flex items-center gap-2">
                <Hash className="h-5 w-5 text-wt-gold" />
                Manage AWB Numbers
              </DialogTitle>
              <DialogDescription className="text-wt-text-secondary">
                {selectedIntegration?.name} - Upload or manually add AWB numbers
              </DialogDescription>
            </DialogHeader>

            <Tabs defaultValue="upload" className="mt-4">
              <TabsList className="bg-wt-paper border border-wt-border">
                <TabsTrigger value="upload" className="data-[state=active]:bg-wt-gold data-[state=active]:text-black">
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Excel
                </TabsTrigger>
                <TabsTrigger value="manual" className="data-[state=active]:bg-wt-gold data-[state=active]:text-black">
                  <FileText className="h-4 w-4 mr-2" />
                  Manual Entry
                </TabsTrigger>
                <TabsTrigger value="list" className="data-[state=active]:bg-wt-gold data-[state=active]:text-black">
                  <List className="h-4 w-4 mr-2" />
                  Current List ({awbList.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="upload" className="mt-4 space-y-4">
                <div className="bg-wt-paper rounded-lg p-6 border border-dashed border-wt-border text-center">
                  <FileSpreadsheet className="h-12 w-12 mx-auto text-wt-text-muted mb-4" />
                  <p className="text-wt-text-secondary mb-4">
                    Upload an Excel or CSV file with AWB numbers
                  </p>
                  <div className="flex justify-center gap-2">
                    <Button
                      variant="outline"
                      onClick={downloadSampleAwbFile}
                      className="border-wt-border text-wt-text-secondary"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download Sample
                    </Button>
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="bg-wt-gold hover:bg-wt-gold-hover text-black"
                    >
                      {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                      Upload File
                    </Button>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleAwbUpload}
                    className="hidden"
                  />
                  <p className="text-xs text-wt-text-muted mt-4">Supported formats: CSV, Excel (.xlsx, .xls)</p>
                </div>
              </TabsContent>

              <TabsContent value="manual" className="mt-4 space-y-4">
                <div className="space-y-2">
                  <Label className="text-wt-text-secondary">Enter AWB Numbers</Label>
                  <Textarea
                    placeholder="Enter AWB numbers (one per line, or comma/space separated)&#10;&#10;Example:&#10;1234567890&#10;1234567891&#10;1234567892"
                    value={awbInput}
                    onChange={(e) => setAwbInput(e.target.value)}
                    className="bg-wt-paper border-wt-border text-wt-text-primary font-mono"
                    rows={8}
                  />
                </div>
                <Button
                  onClick={handleAwbManualAdd}
                  disabled={uploading || !awbInput.trim()}
                  className="bg-wt-gold hover:bg-wt-gold-hover text-black w-full"
                >
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                  Add AWB Numbers
                </Button>
              </TabsContent>

              <TabsContent value="list" className="mt-4 space-y-4">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-wt-text-secondary">
                    {awbList.length} AWB numbers available
                  </p>
                  {awbList.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearAwbList}
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Clear All
                    </Button>
                  )}
                </div>
                <div className="bg-wt-paper rounded-lg p-4 max-h-60 overflow-y-auto">
                  {awbList.length === 0 ? (
                    <p className="text-center text-wt-text-muted py-8">No AWB numbers uploaded yet</p>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {awbList.slice(0, 50).map((awb, idx) => (
                        <div key={idx} className="px-3 py-1.5 bg-wt-surface rounded text-sm font-mono text-wt-text-primary">
                          {awb}
                        </div>
                      ))}
                      {awbList.length > 50 && (
                        <div className="px-3 py-1.5 text-sm text-wt-text-muted">
                          +{awbList.length - 50} more...
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </DialogContent>
        </Dialog>

        {/* Pincode Management Dialog */}
        <Dialog open={pincodeDialogOpen} onOpenChange={setPincodeDialogOpen}>
          <DialogContent className="bg-wt-surface border-wt-border max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-wt-text-primary flex items-center gap-2">
                <MapPin className="h-5 w-5 text-blue-400" />
                Manage Serviceable Pincodes
              </DialogTitle>
              <DialogDescription className="text-wt-text-secondary">
                {selectedIntegration?.name} - Configure which pincodes this courier can service
              </DialogDescription>
            </DialogHeader>

            {/* All Pincodes Toggle */}
            <div className="flex items-center justify-between p-4 bg-wt-paper rounded-lg mt-4">
              <div>
                <p className="text-wt-text-primary font-medium">Enable All Pincodes</p>
                <p className="text-xs text-wt-text-muted">When enabled, this courier can service any pincode</p>
              </div>
              <Switch
                checked={allPincodesEnabled}
                onCheckedChange={handleAllPincodesToggle}
              />
            </div>

            {!allPincodesEnabled && (
              <Tabs defaultValue="upload" className="mt-4">
                <TabsList className="bg-wt-paper border border-wt-border">
                  <TabsTrigger value="upload" className="data-[state=active]:bg-blue-500 data-[state=active]:text-white">
                    <Upload className="h-4 w-4 mr-2" />
                    Upload List
                  </TabsTrigger>
                  <TabsTrigger value="manual" className="data-[state=active]:bg-blue-500 data-[state=active]:text-white">
                    <FileText className="h-4 w-4 mr-2" />
                    Manual Entry
                  </TabsTrigger>
                  <TabsTrigger value="list" className="data-[state=active]:bg-blue-500 data-[state=active]:text-white">
                    <List className="h-4 w-4 mr-2" />
                    Current List ({pincodeList.length})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="upload" className="mt-4 space-y-4">
                  <div className="bg-wt-paper rounded-lg p-6 border border-dashed border-wt-border text-center">
                    <MapPin className="h-12 w-12 mx-auto text-wt-text-muted mb-4" />
                    <p className="text-wt-text-secondary mb-4">
                      Upload an Excel or CSV file with serviceable pincodes
                    </p>
                    <div className="flex justify-center gap-2">
                      <Button
                        variant="outline"
                        onClick={downloadSamplePincodeFile}
                        className="border-wt-border text-wt-text-secondary"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download Sample
                      </Button>
                      <Button
                        onClick={() => pincodeFileInputRef.current?.click()}
                        disabled={uploading}
                        className="bg-blue-500 hover:bg-blue-600 text-white"
                      >
                        {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                        Upload File
                      </Button>
                    </div>
                    <input
                      ref={pincodeFileInputRef}
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handlePincodeUpload}
                      className="hidden"
                    />
                  </div>
                </TabsContent>

                <TabsContent value="manual" className="mt-4 space-y-4">
                  <div className="space-y-2">
                    <Label className="text-wt-text-secondary">Enter Pincodes</Label>
                    <Textarea
                      placeholder="Enter pincodes (one per line, or comma/space separated)&#10;&#10;Example:&#10;110001&#10;110002&#10;400001"
                      value={pincodeInput}
                      onChange={(e) => setPincodeInput(e.target.value)}
                      className="bg-wt-paper border-wt-border text-wt-text-primary font-mono"
                      rows={8}
                    />
                  </div>
                  <Button
                    onClick={handlePincodeManualAdd}
                    disabled={uploading || !pincodeInput.trim()}
                    className="bg-blue-500 hover:bg-blue-600 text-white w-full"
                  >
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                    Add Pincodes
                  </Button>
                </TabsContent>

                <TabsContent value="list" className="mt-4 space-y-4">
                  <div className="flex justify-between items-center">
                    <p className="text-sm text-wt-text-secondary">
                      {pincodeList.length} pincodes configured
                    </p>
                    {pincodeList.length > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={clearPincodeList}
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Clear All
                      </Button>
                    )}
                  </div>
                  <div className="bg-wt-paper rounded-lg p-4 max-h-60 overflow-y-auto">
                    {pincodeList.length === 0 ? (
                      <p className="text-center text-wt-text-muted py-8">No pincodes configured yet</p>
                    ) : (
                      <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                        {pincodeList.slice(0, 100).map((pincode, idx) => (
                          <div key={idx} className="px-3 py-1.5 bg-wt-surface rounded text-sm font-mono text-wt-text-primary text-center">
                            {pincode}
                          </div>
                        ))}
                        {pincodeList.length > 100 && (
                          <div className="px-3 py-1.5 text-sm text-wt-text-muted">
                            +{pincodeList.length - 100} more...
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default CustomCourierIntegrationPage;
